<template>
  <div>
    <v-container v-if="psp && !loadingFetch">

      <!-- ── Sección Precio ─────────────────────────────────── -->
      <v-toolbar flat color="transparent" class="px-0">
        <v-toolbar-title><h3>Precio</h3></v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          rounded
          :disabled="!precioDirty || loadingPrecio"
          :loading="loadingPrecio"
          @click="guardarPrecio"
        >
          Guardar
        </v-btn>
      </v-toolbar>

      <v-alert v-if="errorPrecio" type="error" dense text class="mb-3">
        {{ errorPrecio }}
      </v-alert>

      <v-row class="mt-2">
        <v-col cols="12">
          <GenericAttributeInput
            attributeName="precioCosto"
            label="Precio de Costo"
            type="number"
            :rules="'required|decimal:2|max:30'"
            :value="String(precioCostoLocal)"
            :outlined="true"
            @update-attribute="handlePrecioCostoUpdate"
          />
          <GenericAttributeInput
            attributeName="ganancia"
            label="Margen de ganancia"
            type="number"
            prefix="%"
            :rules="'required|decimal:2|max:30'"
            :value="String(gananciaLocal)"
            :outlined="true"
            @update-attribute="handleGananciaUpdate"
          />
          <PrecioVentaCalculatorWrapper
            :precioCosto="String(precioCostoLocal)"
            :ganancia="String(gananciaLocal)"
            :precioVenta="String(precioVentaLocal)"
            :precioBase="String(psp.precio_base)"
            :esPrecioVentaManual="true"
            :disabled="false"
            :iva="rubroVentaLocal && rubroVentaLocal.iva ? rubroVentaLocal.iva : (psp.iva || { description: '0' })"
            :outlined="true"
            ref="precioVentaWrapper"
            @update:precioVenta="precioVentaLocal = $event"
            @update:ganancia="gananciaLocal = $event"
          />
        </v-col>
      </v-row>

      <v-divider class="my-5" />

      <!-- ── Sección Contabilidad ───────────────────────────── -->
      <v-toolbar flat color="transparent" class="px-0">
        <v-toolbar-title><h3>Configuración Contable</h3></v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          rounded
          :disabled="!contabilidadDirty || loadingContabilidad"
          :loading="loadingContabilidad"
          @click="guardarContabilidad"
        >
          Guardar
        </v-btn>
      </v-toolbar>

      <v-alert v-if="errorContabilidad" type="error" dense text class="mb-3">
        {{ errorContabilidad }}
      </v-alert>

      <ProductoSucursalContableUpdate
        :rubroVenta="rubroVentaLocal"
        :rubroCompra="rubroCompraLocal"
        @update:rubroVenta="rubroVentaLocal = $event"
        @update:rubroCompra="rubroCompraLocal = $event"
      />

    </v-container>

    <v-container v-else-if="loadingFetch" class="text-center py-10">
      <v-progress-circular indeterminate />
    </v-container>

    <v-container v-else>
      <p>Sin datos</p>
    </v-container>
  </div>
</template>

<script>
import ProductosAPI from "@/api/productos";
import GenericAttributeInput from "@/components/util/GenericAttributeInput.vue";
import PrecioVentaCalculatorWrapper from "@/components/producto/form/PrecioVentaCalculatorWrapper.vue";
import ProductoSucursalContableUpdate from "@/views/producto/actualizar/ProductoSucursalContableUpdate.vue";

export default {
  name: "ProductoSucursalUpdate",
  components: {
    GenericAttributeInput,
    PrecioVentaCalculatorWrapper,
    ProductoSucursalContableUpdate,
  },
  $_veeValidate: {
    validator: "new",
  },
  props: {
    productoProp: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loadingFetch: false,
      psp: null,

      // Estado local Precio
      precioCostoLocal: null,
      precioCostoOriginal: null,
      precioVentaLocal: null,
      precioVentaOriginal: null,
      gananciaLocal: null,
      gananciaOriginal: null,
      loadingPrecio: false,
      errorPrecio: null,

      // Estado local Contabilidad
      rubroVentaLocal: null,
      rubroCompraLocal: null,
      rubroVentaOriginal: null,
      rubroCompraOriginal: null,
      loadingContabilidad: false,
      errorContabilidad: null,
    };
  },
  computed: {
    precioDirty() {
      return (
        parseFloat(this.precioCostoLocal) !== parseFloat(this.precioCostoOriginal) ||
        parseFloat(this.precioVentaLocal) !== parseFloat(this.precioVentaOriginal) ||
        parseFloat(this.gananciaLocal) !== parseFloat(this.gananciaOriginal)
      );
    },
    contabilidadDirty() {
      const rvId = this.rubroVentaLocal ? this.rubroVentaLocal.id : null;
      const rcId = this.rubroCompraLocal ? this.rubroCompraLocal.id : null;
      const rvOrigId = this.rubroVentaOriginal ? this.rubroVentaOriginal.id : null;
      const rcOrigId = this.rubroCompraOriginal ? this.rubroCompraOriginal.id : null;
      return rvId !== rvOrigId || rcId !== rcOrigId;
    },
  },
  methods: {
    initFromPsp(data) {
      this.psp = data;

      this.precioCostoLocal = data.precio_costo;
      this.precioCostoOriginal = data.precio_costo;
      this.precioVentaLocal = data.precio_venta;
      this.precioVentaOriginal = data.precio_venta;
      this.gananciaLocal = data.ganancia;
      this.gananciaOriginal = data.ganancia;

      const contabilidad = data.contabilidad || {};
      this.rubroVentaLocal = contabilidad.rubro_venta ? { ...contabilidad.rubro_venta } : null;
      this.rubroCompraLocal = contabilidad.rubro_compra ? { ...contabilidad.rubro_compra } : null;
      this.rubroVentaOriginal = this.rubroVentaLocal ? { ...this.rubroVentaLocal } : null;
      this.rubroCompraOriginal = this.rubroCompraLocal ? { ...this.rubroCompraLocal } : null;
    },

    handlePrecioCostoUpdate({ value }) {
      this.precioCostoLocal = value;
      const costo = parseFloat(value) || 0;
      const g = parseFloat(this.gananciaLocal) / 100 || 0;
      const iva = parseFloat(this.psp.iva?.description) / 100 || 0;
      if (costo > 0) {
        this.precioVentaLocal = parseFloat((costo * (1 + g) * (1 + iva)).toFixed(2));
      }
    },

    // Cuando el usuario edita ganancia manualmente, recalcula precio_venta
    handleGananciaUpdate({ value }) {
      this.gananciaLocal = value;
      const costo = parseFloat(this.precioCostoLocal) || 0;
      const g = parseFloat(value) / 100 || 0;
      const iva = parseFloat(this.psp.iva?.description) / 100 || 0;
      if (costo > 0) {
        this.precioVentaLocal = parseFloat((costo * (1 + g) * (1 + iva)).toFixed(2));
      }
    },

    guardarPrecio() {
      this.$validator.validateAll().then((valid) => {
        if (!valid) return;

        this.loadingPrecio = true;
        this.errorPrecio = null;

        ProductosAPI.patchPspPrecio(this.psp.id, {
              precio_venta: this.precioVentaLocal,
              precio_costo: this.precioCostoLocal,
              ganancia: this.gananciaLocal,
            })
          .then((response) => {
            this.initFromPsp(response.data);
            this.$updateBus.$emit("showSnackBar", {
              show: true,
              button: false,
              color: "success",
              text: "Precio actualizado",
              icon: "mdi-check",
              timeout: 2000,
            });
          })
          .catch(() => {
            this.errorPrecio = "No se pudo actualizar el precio. Intentá de nuevo.";
          })
          .finally(() => {
            this.loadingPrecio = false;
          });
      });
    },

    guardarContabilidad() {
      this.loadingContabilidad = true;
      this.errorContabilidad = null;

      const payload = {
        rubro_venta_id: this.rubroVentaLocal ? this.rubroVentaLocal.id : null,
        rubro_compra_id: this.rubroCompraLocal ? this.rubroCompraLocal.id : null,
      };

      ProductosAPI.patchPspContabilidad(this.psp.id, payload)
        .then((response) => {
          this.initFromPsp(response.data);
          this.$updateBus.$emit("showSnackBar", {
            show: true,
            button: false,
            color: "success",
            text: "Contabilidad actualizada",
            icon: "mdi-check",
            timeout: 2000,
          });
        })
        .catch(() => {
          this.errorContabilidad = "No se pudo actualizar la contabilidad. Intentá de nuevo.";
        })
        .finally(() => {
          this.loadingContabilidad = false;
        });
    },
  },
  watch: {
    productoProp: {
      handler(newVal) {
        if (newVal) {
          this.initFromPsp(newVal);
        } else {
          this.psp = null;
        }
      },
      immediate: true,
    },
  },
};
</script>
